import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Dialog, Flex, Spinner, Theme, VisuallyHidden } from "@radix-ui/themes";
import { useLDClient } from "@launchdarkly/react-sdk";
import { CognitoAuthProvider, RequireAuth } from "../lib/CognitoAuthProvider";
import { WaitlistConfirmation } from "./WaitlistConfirmation";
import { TitleBlock } from "../routes/TitleBlock";
import { IrisCtaLink } from "./IrisCtaLink";
import { isCurrentUserOffWaitlist } from "../lib/waitlistUser";
import { WaitlistModalContext, useWaitlistModal } from "./waitlistModalContext";

export { useWaitlistModal };

/**
 * Rendered once the user has authenticated inside the modal.
 *
 * `justSignedUp` short-circuits the status check: a brand-new account created
 * through the waitlist flow is always "waiting" (their provisioning, which
 * writes waitlist_status, runs asynchronously and hasn't happened yet), so we
 * must NOT call the status API - it would return no status and be misread as
 * "admitted". Existing users signing in are re-checked: someone who signed in
 * while the flag was on but turns out to be already admitted (or never on the
 * waitlist) should see the normal iMessage CTA, not the "you're on the
 * waitlist" message.
 */
function WaitlistOutcome({ justSignedUp }: { justSignedUp: boolean }) {
  // useLDClient lets the CTA resolve the same iMessage deep link the home
  // hero CTA uses; guarded for the (non-prerendered) safety of the check.
  const ldClient = typeof window !== "undefined" ? useLDClient() : undefined;
  const [status, setStatus] = useState<"loading" | "waiting" | "offWaitlist">(
    justSignedUp ? "waiting" : "loading",
  );

  useEffect(() => {
    // A freshly-created waitlist account is always waiting; never hit the API.
    if (justSignedUp) {
      setStatus("waiting");
      return;
    }

    let isMounted = true;
    isCurrentUserOffWaitlist().then((offWaitlist) => {
      if (isMounted) {
        setStatus(offWaitlist ? "offWaitlist" : "waiting");
      }
    });
    return () => {
      isMounted = false;
    };
  }, [justSignedUp]);

  if (status === "loading") {
    return (
      <Flex align="center" justify="center" style={{ minHeight: 180 }}>
        <Spinner size="3" />
      </Flex>
    );
  }

  if (status === "offWaitlist") {
    return (
      <Flex direction="column" gap="4" align="center">
        <TitleBlock
          title="You're In!"
          subtitle="You've been admitted to Iris. Start the conversation whenever you're ready."
          innerStyle={{
            minHeight: "auto",
            padding: "40px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          headingStyle={{
            fontSize: 27,
            fontWeight: 400,
            lineHeight: "30px",
          }}
        >
          <IrisCtaLink
            disableWaitlist
            ldClient={ldClient ?? undefined}
            overridePhone={
              import.meta.env.VITE_PUBLIC_IRIS_HOME_PAGE_PHONE_NUMBER
            }
          />
        </TitleBlock>
      </Flex>
    );
  }

  return <WaitlistConfirmation />;
}

/**
 * Provides the waitlist modal to a subtree and renders a single shared dialog.
 * Mount this high enough to cover every surface with a waitlist CTA (including
 * the `/welcome` early return in App). The heavy auth subtree only mounts
 * while the dialog is open.
 */
export function WaitlistModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Whether the current in-modal authentication just created a brand-new
  // account (vs. an existing user signing in). Drives WaitlistOutcome's
  // decision to skip the status API check for fresh signups. Reset each time
  // the modal opens so a stale signup flag can't leak into a later session.
  const [justSignedUp, setJustSignedUp] = useState(false);

  const openWaitlistModal = useCallback(() => {
    setJustSignedUp(false);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openWaitlistModal }), [openWaitlistModal]);

  return (
    <WaitlistModalContext.Provider value={value}>
      {children}
      {/* Wrapped in its own Theme because the dialog is rendered here, above
          the per-page <Theme> in App, and its Radix Themes contents need a
          Theme ancestor to resolve their color/radius tokens. */}
      <Theme
        appearance="light"
        accentColor="blue"
        grayColor="gray"
        radius="large"
      >
        {/* CognitoAuthProvider is mounted here, ABOVE Dialog.Root, so the
            session check runs while the page is idle - not when the modal
            opens. By the time Dialog.Content mounts, the auth context is
            already `ready`, so RequireAuth renders the sign-in form on its
            first frame instead of briefly rendering null and then resizing
            the card mid-animation (which made the entrance stutter). */}
        <CognitoAuthProvider>
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Content
              className="waitlist-modal-content"
              aria-describedby={undefined}
            >
              <VisuallyHidden>
                <Dialog.Title>Join the Waitlist</Dialog.Title>
              </VisuallyHidden>
              <Dialog.Close>
                <button
                  type="button"
                  className="waitlist-modal-close"
                  aria-label="Close"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </Dialog.Close>
              <RequireAuth
                intent="waitlist"
                onAuthenticated={(mode) => setJustSignedUp(mode === "signUp")}
              >
                <WaitlistOutcome justSignedUp={justSignedUp} />
              </RequireAuth>
            </Dialog.Content>
          </Dialog.Root>
        </CognitoAuthProvider>
      </Theme>
    </WaitlistModalContext.Provider>
  );
}
